/**
 * Emparejamiento de soportes contables con documentos de la carpeta.
 *
 * Lo usa la contabilizacion masiva (documents/bulk_accounting.js) para
 * sugerir a que documento pertenece cada archivo contable arrastrado.
 *
 * Dos criterios, en orden estricto:
 *
 *  1. NOMBRE EXACTO (sin extension, sin acentos, minusculas). Es el
 *     criterio historico y siempre gana.
 *  2. NUMERO, solo si el nombre exacto no dio nada. Cubre el caso real
 *     de produccion (2026-09-21): los soportes llegan como
 *     "FE-00009151.pdf" y los documentos en la carpeta se llaman
 *     "FVE9151.pdf" — mismo numero de factura, prefijo y ceros
 *     distintos. Sin esto el usuario asigna a mano archivo por archivo.
 *
 * Por que es conservador: este modulo decide a que documento queda
 * pegado un soporte contable. Un emparejamiento equivocado pega el
 * soporte de una factura a otra. Entonces:
 *
 *  - Numeros de menos de 3 digitos significativos NO emparejan (un
 *    "Anexo 1" no debe caer sobre un "Soporte 1").
 *  - Si el numero da mas de un candidato (el caso de una nota credito
 *    NC9151 conviviendo con la factura FVE9151), este modulo devuelve
 *    los dos y quien llama NO asigna nada: queda para asignacion manual.
 *  - Los documentos que ya tienen contabilizacion nunca son candidatos;
 *    se reportan aparte (`already`) para poder explicarselo al usuario.
 *
 * Modulo puro: sin React/Redux/Firestore, para poder testearlo directo.
 */

// Diacriticos combinantes U+0300–U+036F (escrito con escapes para que la
// codificacion del archivo no pueda alterar el rango).
const DIACRITICS_RE = new RegExp('[\\u0300-\\u036f]', 'g');

// Minimo de digitos significativos (ya sin ceros a la izquierda) para
// considerar que un numero identifica una factura.
const MIN_SIGNIFICANT_DIGITS = 3;

const withoutExtension = (name) => {
  const s = String(name || '').trim();
  const i = s.lastIndexOf('.');
  return i > 0 ? s.slice(0, i) : s; // no rompe nombres tipo ".dotfile"
};

/**
 * Clave de emparejamiento exacto: nombre sin extension, sin acentos,
 * minusculas. "FVE-1465.pdf" y "fve-1465.XLSX" producen la misma clave.
 */
export const baseKey = (name) =>
  withoutExtension(name)
    .normalize('NFD')
    .replace(DIACRITICS_RE, '')
    .normalize('NFC')
    .trim()
    .toLowerCase();

/**
 * Numero de factura deducido del nombre, o null si no hay uno confiable.
 *
 * Toma el grupo de digitos MAS LARGO (en empate, el ultimo) y le quita
 * los ceros a la izquierda. Asi "FE-2026-00009151" da 9151 y no 2026, y
 * "FVE9151-v2" da 9151 y no 2.
 */
export const numberKey = (name) => {
  const groups = withoutExtension(name).match(/\d+/g);
  if (!groups) return null;

  let best = '';
  for (const g of groups) {
    if (g.length >= best.length) best = g; // >= : en empate gana el ultimo
  }

  const significant = best.replace(/^0+/, '');
  return significant.length >= MIN_SIGNIFICANT_DIGITS ? significant : null;
};

const push = (map, key, value) => {
  if (!map.has(key)) map.set(key, []);
  map.get(key).push(value);
};

/**
 * Indexa los documentos de la carpeta para consultarlos por nombre y por
 * numero. Separa los elegibles de los que ya tienen contabilizacion.
 *
 * @param {Array<{key:string,name:string,hasAccounting:boolean}>} targets
 */
export const buildTargetIndex = (targets = []) => {
  const byBase = new Map();       // base   -> targets elegibles
  const byNumber = new Map();     // numero -> targets elegibles
  const takenBases = new Set();   // bases de documentos ya contabilizados
  const takenNumbers = new Set(); // idem por numero

  for (const t of targets || []) {
    if (!t) continue;
    const base = baseKey(t.name);
    const number = numberKey(t.name);

    if (t.hasAccounting) {
      takenBases.add(base);
      if (number) takenNumbers.add(number);
      continue;
    }

    push(byBase, base, t);
    if (number) push(byNumber, number, t);
  }

  return { byBase, byNumber, takenBases, takenNumbers };
};

/**
 * Candidatos para un archivo contable.
 *
 * @returns {{kind:'exact'|'numeric'|'already'|'none', candidates:Array, number:string|null}}
 *   exact   → coincide el nombre completo (1 candidato normalmente; mas
 *             de uno significa nombres duplicados en la carpeta)
 *   numeric → coincide solo el numero. Si trae mas de un candidato, quien
 *             llama debe tratarlo como ambiguo y no asignar.
 *   already → lo unico que coincide ya tiene contabilizacion
 *   none    → nada que proponer
 */
export const findCandidates = (fileName, index) => {
  const { byBase, byNumber, takenBases, takenNumbers } = index || {};
  const base = baseKey(fileName);
  const number = numberKey(fileName);

  const exact = (byBase && byBase.get(base)) || [];
  if (exact.length) return { kind: 'exact', candidates: exact, number };

  if (takenBases && takenBases.has(base)) {
    return { kind: 'already', candidates: [], number };
  }

  if (number) {
    const numeric = (byNumber && byNumber.get(number)) || [];
    if (numeric.length) return { kind: 'numeric', candidates: numeric, number };
    if (takenNumbers && takenNumbers.has(number)) {
      return { kind: 'already', candidates: [], number };
    }
  }

  return { kind: 'none', candidates: [], number };
};
